import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { Timer } from "../../components/index.js";
import { questionTypeMap } from "../../constants/QuestionsBankConstants.js";
import {
  createResult,
  formatDate,
  getSingleAssessment,
  getSingleQuestion,
  markResult,
  updateResult,
} from "../../helpers/backend_helper_2";
import "./AssessmentTaking.scss";

const AssessmentTaking = () => {
  const [modal, setModal] = useState(true);
  const [terms, setTerms] = useState(false);
  const [assessmentData, setAssessmentData] = useState({});
  const [questionData, setQuestionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testComplete, setTestComplete] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [resultID, setResultID] = useState(0);

  const answersStorage = useRef([]);
  const navigate = useNavigate();
  const testId = 1;
  const candidateId = 4;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const results = await getSingleAssessment(
          testId,
          setLoading,
          setAssessmentData
        );
        if (results) {
          const fetchedQuestions = await Promise.all(
            results.testQuestions.map(async (question) => {
              return await getSingleQuestion(question.questionId, setLoading);
            })
          );
          setQuestionData(fetchedQuestions);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  useEffect(() => {
    if (questionData.length > 0) {
      // Initialize answers based on question types
      const initialAnswers = questionData.map((questionData) => {
        switch (questionData.questiontype) {
          case "MCQ":
            return []; // Array for multiple selections
          case "TF":
            return ""; // String for true/false
          default:
            return ""; // String for text answers
        }
      });
      setUserAnswers(initialAnswers);
    }
  }, [questionData]); // Run when questions data changes

  // Update answer storage and check completion
  useEffect(() => {
    // Update the ref with current answers
    answersStorage.current = [...userAnswers];

    // Check completion status
    const isComplete = userAnswers.every((answer, index) => {
      const question = questionData[index];
      if (!question) return false;

      switch (question.questiontype) {
        case "MCQ":
          return answer.length > 0;
        case "TF":
          return answer !== "";
        default:
          return answer.trim() !== "";
      }
    });

    setAllQuestionsAnswered(isComplete);
  }, [userAnswers, questionData]);

  const StartAssessment = () => {
    if (!terms) {
      alert("Please accept the terms and conditions to proceed");
      return;
    }

    if (timerRef.current) {
      timerRef.current.start();
    }
    setModal(false);
    createResult(
      timerRef.current?.getElapsedUpdate(),
      testId,
      setResultID,
      candidateId,
      answersStorage.current,
      setError
    );
  };

  const cancel = () => {
    navigate(-1);
  };

  const endTest = () => {
    setTestComplete(true);
    timerRef.current.pause();

    markResult(
      timerRef.current?.getElapsedUpdate(),
      testId,
      resultID,
      candidateId,
      answersStorage.current
    );
  };

  const timerRef = useRef();

  const handleNext = () => {
    if (currentQuestionIndex < questionData.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
      updateResult(
        timerRef.current?.getElapsedUpdate(),
        testId,
        resultID,
        candidateId,
        answersStorage.current
      );
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
      updateResult(
        timerRef.current?.getElapsedUpdate(),
        testId,
        resultID,
        candidateId,
        answersStorage.current
      );
    }
  };

  const getExpiryTimestamp = () => {
    const time = new Date();
    return time.setSeconds(
      time.getSeconds() +
        (assessmentData.durationHr || 0) * 3600 +
        (assessmentData.durationMin || 0) * 60
    );
  };

  const currentQuestion = questionData[currentQuestionIndex];
  const relatedTestQuestion = assessmentData.testQuestions?.find(
    (testQuestion) => testQuestion.order === currentQuestionIndex + 1
  );

  if (loading) {
    return (
      <div className="text-center">
        <h5>Loading...</h5>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <h5>An error has occured</h5>
      </div>
    );
  }

  return (
    <>
      <Modal isOpen={modal} size="lg">
        <ModalHeader toggle={cancel} />
        <ModalBody toggle={cancel}>
          <Card style={{ margin: "5px" }}>
            <CardHeader className="logo-header">
              <span className="mdi mdi-timer-outline">
                Duration {assessmentData.durationHr}:
                {assessmentData.durationMin} min
              </span>
            </CardHeader>
            <CardBody>
              <h5>Acknowledgement & Instruction</h5>
              <p>
                <span>
                  Thanks for your interest in Aven system. Your next step is to
                  complete this assessment. Before you start, you can click the
                  Aven system Candidate Online Assessment Privacy Notice to
                  learn more about how your data will be kept private. We want
                  to ensure that the assessment experience is fair for everyone
                  so, for test security purposes, Aven system uses mechanisms to
                  detect suspicious test behavior and responses. If suspicious
                  behavior is detected (e.g., attempts to plagiarize, fabricate,
                  or use outside assistance), it will impact your current and
                  possibly future Aven system applications. Possible responses
                  include invalidating your assessment, requiring additional
                  assessments, ineligibility for the current or future roles,
                  and/or legal action.
                </span>
              </p>
              <span>
                Please check the box below to indicate you understand and agree
                to the following:
              </span>
              <br />
              <ol>
                <li>I am the person who is applying for this position.</li>
                <li>
                  Responses to all assessments in this application process are
                  my own and not prepared by another person or group.
                </li>
                <li>
                  I will not use unauthorized materials, websites, or other
                  sources of information during this assessment.
                </li>
                <li>
                  I will not share the items or other content in this assessment
                  with other individuals or groups.
                </li>
                <li>
                  I will not consult any other person or group while completing
                  the assessment.
                </li>
                <li>
                  I understand that any misrepresentation will result in the
                  rejection of my application.
                </li>
                <li>
                  I understand that if Aven system detects suspicious
                  test-taking behavior, Aven system may invalidate my
                  assessment, require additional assessments, deem me ineligible
                  for employment, or take legal action.
                </li>
              </ol>
            </CardBody>
          </Card>
        </ModalBody>
        <div className="d-flex justify-content-center align-items-center mb-3">
          <Input type="checkbox" onChange={() => setTerms(!terms)} />
          <Label style={{ marginLeft: "7px" }}>
            By Clicking this check box your accepting the above consent and T&C.
          </Label>
        </div>
        <div className="d-flex justify-content-end mt-auto">
          <Button
            style={{ backgroundColor: "white", color: "black" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#0FBA67", color: "white" }}
            onClick={StartAssessment}
          >
            Start{" "}
          </Button>
        </div>
      </Modal>
      <div className={modal ? "blur-bg" : ""}>
        {/* info bar */}
        <div className="info-bar">
          <h5>{assessmentData.assessmentName}</h5>
          <div className="d-flex justify-content-center align-items-center">
            <Card className="info-card">
              <div className="d-flex align-items-center gap-2">
                <span className="mdi mdi-file-document-outline" />
                <span>{assessmentData.testQuestions?.length}</span>
                <span style={{ color: "black" }}>Questions</span>
              </div>
            </Card>
            <Card className="info-card">
              <div className="d-flex align-items-center gap-2">
                <span className=" mdi mdi-timer-outline" />
                <Timer
                  ref={timerRef}
                  expiryTimestamp={getExpiryTimestamp()}
                  setTestComplete={setTestComplete}
                  endTest={endTest}
                />
                <span style={{ color: "black" }}></span>
              </div>
            </Card>
            <Card className="info-card ">
              <div className="d-flex align-items-center gap-2">
                <span className="mdi mdi-calendar-blank-outline"></span>
                <span style={{ color: "black" }}>
                  {" "}
                  Expire on {formatDate(assessmentData.expiryDate)}
                </span>
              </div>
            </Card>

            {allQuestionsAnswered && (
              <Button
                onClick={() => {
                  endTest();
                }}
              >
                Finish
              </Button>
            )}
          </div>
        </div>

        {!testComplete ? (
          questionData.length > 0 ? (
            <div className="d-flex">
              {/* Main Question Area */}
              <div
                style={{
                  width: "80% ",
                  height: "300px",
                  margin: "15%",
                  marginTop: "20%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div>
                  <h2 style={{ textAlign: "center" }}>
                    Q{currentQuestionIndex + 1}.{" "}
                    {currentQuestion?.questionTitle}
                  </h2>
                </div>
                <div className="d-flex justify-content-center align-items-center gap-2 m-1">
                  <span className="align-self-center">
                    {questionTypeMap[currentQuestion?.questiontype]}
                  </span>
                  <Card className="marksWeightage">
                    {relatedTestQuestion?.weightage} marks
                  </Card>
                </div>
                <div>
                  {/* Answer Fields */}

                  {currentQuestion?.questiontype === "MCQ" ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                      }}
                      className="d-flex justify-content-center align-items-center gap-2 m-1"
                    >
                      {currentQuestion.answers[0].Options.map(
                        (option, optionIndex) => (
                          <Card className="option-card" key={optionIndex}>
                            <div className="d-flex align-items-center gap-2">
                              <Input
                                type="checkbox"
                                checked={userAnswers[
                                  currentQuestionIndex
                                ]?.includes(option)}
                                onChange={() => {
                                  const updatedAnswers = [...userAnswers];
                                  const currentSelections =
                                    updatedAnswers[currentQuestionIndex] || [];

                                  if (currentSelections.includes(option)) {
                                    updatedAnswers[currentQuestionIndex] =
                                      currentSelections.filter(
                                        (v) => v !== option
                                      );
                                  } else {
                                    updatedAnswers[currentQuestionIndex] = [
                                      ...currentSelections,
                                      option,
                                    ];
                                  }
                                  setUserAnswers(updatedAnswers);
                                }}
                              />
                              <span>{option}</span>
                            </div>
                          </Card>
                        )
                      )}
                    </div>
                  ) : currentQuestion?.questiontype === "TF" ? (
                    <div className="d-flex justify-content-center align-items-center gap-2 m-1">
                      <Card className="option-card">
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            type="radio"
                            name="tf"
                            value="True"
                            checked={
                              userAnswers[currentQuestionIndex] === "True"
                            }
                            onChange={(e) => {
                              const updatedAnswers = [...userAnswers];
                              updatedAnswers[currentQuestionIndex] =
                                e.target.value;
                              setUserAnswers(updatedAnswers);
                            }}
                          />
                          <span>True</span>
                        </div>
                      </Card>
                      <Card className="option-card">
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            type="radio"
                            name="tf"
                            value="False"
                            checked={
                              userAnswers[currentQuestionIndex] === "False"
                            }
                            onChange={(e) => {
                              const updatedAnswers = [...userAnswers];
                              updatedAnswers[currentQuestionIndex] =
                                e.target.value;
                              setUserAnswers(updatedAnswers);
                            }}
                          />
                          <span>False</span>
                        </div>
                      </Card>
                    </div>
                  ) : (
                    <Input
                      type="textarea"
                      cols="50"
                      rows="3"
                      style={{ border: "1px solid #C2C9D1" }}
                      value={userAnswers[currentQuestionIndex] || ""}
                      onChange={(e) => {
                        const updatedAnswers = [...userAnswers];
                        updatedAnswers[currentQuestionIndex] = e.target.value;
                        setUserAnswers(updatedAnswers);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Pagination Sidebar */}
              <div
                style={{ borderLeft: "1px solid #C2C9D1", height: "fill" }}
                className="d-flex flex-column justify-content-center align-items-center "
              >
                <div
                  style={{
                    width: "auto",
                    height: "auto",
                    transform: "rotate(90deg)",
                    marginBottom: "90px",
                  }}
                  className="d-flex flex-row justify-content-center align-items-center "
                >
                  <Button
                    className="pagination-button"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                  >
                    <span className="mdi mdi-arrow-left" />
                    <span style={{ marginLeft: "5px" }}>Previous</span>
                  </Button>
                  <Card style={{ marginTop: "23.5px" }}>
                    <CardBody
                      style={{ height: "40px", transform: "rotate(-90deg)" }}
                    >
                      {currentQuestionIndex + 1}/{questionData.length}
                    </CardBody>
                  </Card>
                  <Button
                    className="pagination-button"
                    onClick={handleNext}
                    disabled={currentQuestionIndex === questionData.length - 1}
                  >
                    <span style={{ marginRight: "5px" }}>Next</span>
                    <span className="mdi mdi-arrow-right" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <h5>No questions available.</h5>
            </div>
          )
        ) : (
          <div
            style={{ backgroundColor: "white", height: "100vh" }}
            className="d-flex flex-column justify-content-top align-items-center"
          >
            <div
              style={{
                borderBottom: "1px solid #B2B2B2",
                padding: "20px",
                width: "100%",
              }}
            >
              <h4 style={{ color: "#0965CC" }}>
                Thank you Candidate Name for attending the interview with us.
              </h4>
            </div>
            <div className="d-flex flex-column justify-content-center align-items-center m-5">
              <p>
                We truly appreciate your interest in the role and the effort you
                put into showcasing your skills and experience. We will review
                the results and get back to you with the outcome shortly. If you
                have any questions in the meantime, feel free to reach out.
              </p>
              <h4>Assessment Completed</h4>
              <span>Total Duration</span>
              <span>{timerRef.current?.getElapsedTime()}</span>
              <Button style={{ color: "white", backgroundColor: "#0965CC" }}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AssessmentTaking;
